import { useQuery } from '@tanstack/react-query';
import type { DesignTool } from '@mg/shared';
import { designToolLabel } from '@mg/shared';
import { api } from '../../lib/api';

/**
 * The design-tools view's cache key. App Settings → AI models invalidates it after every save, so Generate
 * media follows image generation being switched on or off at once (D3). One constant for both, so a
 * rename can't leave the dialog reading a cache the card no longer refreshes.
 */
export const DESIGN_TOOLS_QUERY_KEY = ['design-tools'] as const;

/**
 * Which design tool the member-facing buttons should offer, and what to call it.
 *
 * The `{canva_figma}` label in the button copy is singular while both integrations can be
 * configured at once, so the API decides precedence rather than each button guessing. Returns
 * `tool: null` when neither is set up — every caller then renders nothing, which is why this hook
 * exists instead of three copies of the same conditional.
 *
 * The same member-readable view also says whether the built-in image generator is switched on for
 * this install, which Generate media needs and members can't read anywhere else.
 */
export function useDesignTools(): {
  tool: DesignTool | null;
  label: string;
  /** Canva is connected but its grant predates the export scope — import will fail until reconnect. */
  needsCanvaReconnect: boolean;
  /**
   * Generate media may offer the built-in generator (Smorgas Social · FLUX): the install has image
   * generation switched on (App Settings → AI models, else AZURE_OPENAI_DEPLOYMENT_IMAGE).
   *
   * True unless the server SAYS the built-in generator is off. While loading, or against an older
   * server, the dialog behaves as it always did.
   */
  builtInImageGeneration: boolean;
  isLoading: boolean;
} {
  const query = useQuery({
    queryKey: DESIGN_TOOLS_QUERY_KEY,
    queryFn: ({ signal }) => api.getDesignTools(signal),
    // Install-level and changes rarely; no need to refetch on every mount of every button.
    staleTime: 5 * 60_000,
  });

  const data = query.data;
  const tool = data?.preferred ?? null;
  return {
    tool,
    label: tool ? designToolLabel[tool] : '',
    needsCanvaReconnect: Boolean(data?.canvaConnected && !data.canvaCanImport),
    // Only an answer that says false: the server refuses a switched-off generation in any case.
    builtInImageGeneration: data?.builtInImageGeneration !== false,
    isLoading: query.isLoading,
  };
}
